import {isFilled} from '@prismicio/client'
import {PrismicNextImage, PrismicNextLink} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {PricingSectionSliceModulesAndAddonsPrimaryModulesItem} from 'prismicio-types'
import {Icon} from 'src/common/Icon'
import {TextLink} from 'src/common/TextLink'

type ModuleCardProps = {
  module: PricingSectionSliceModulesAndAddonsPrimaryModulesItem
  index: number
}

export const ModuleCard = ({module}: ModuleCardProps) => {
  const {linkResolver} = useLinkResolver()

  return (
    <div className="flex h-full flex-col justify-between gap-6 rounded-lg border border-black/[10%] bg-moss-white p-6 md:max-lg:flex-row">
      <div className="flex flex-1 flex-col gap-4">
        {isFilled.keyText(module.eyebrow) && (
          <p className="text-12 font-semibold uppercase leading-normal tracking-[0.72px] text-moss-blue">
            {module.eyebrow}
          </p>
        )}

        {/* Icon and Title */}
        <div className="flex items-center gap-4">
          {isFilled.image(module.icon) && (
            <div className="relative flex size-8 items-center justify-center rounded-[4px] bg-green-neon">
              <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%]" />
              <PrismicNextImage field={module.icon} className="size-4" />
            </div>
          )}
          {isFilled.keyText(module.module_name) && (
            <h3 className="text-h4 font-medium text-moss-black">{module.module_name}</h3>
          )}
        </div>

        {isFilled.richText(module.rich_description) && (
          <div className="flex flex-col gap-2">
            <PrismicRichText
              field={module.rich_description}
              components={{
                list: ({children}) => <ul className="flex flex-col gap-2">{children}</ul>,
                listItem: ({children}) => (
                  <li className="flex items-center gap-3">
                    <span className="h-[5px] w-[5px] shrink-0 rounded-[1px] bg-moss-grey-300" />
                    <span className="text-15 leading-5 text-moss-grey-500">{children}</span>
                  </li>
                ),
                paragraph: ({children}) => <p className="text-15 leading-5 text-moss-grey-550">{children}</p>,
              }}
            />
          </div>
        )}

        {!isFilled.richText(module.rich_description) && isFilled.keyText(module.description) && (
          <p className="text-tg-body-m pb-2 text-grey-550">{module.description}</p>
        )}

        {/* Learn More Link */}
        {isFilled.link(module.link) &&
          module.link.text &&
          (module.use_green_arrow_link_variant ? (
            <PrismicNextLink
              href={linkResolver(module.link)}
              className="group mt-auto flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100"
            >
              <span className="text-button-s uppercase text-black">{module.link.text}</span>
              <div className="flex size-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
                <Icon name="arrow" className="h-2 w-2.5 text-green-dark" aria-hidden="true" />
              </div>
            </PrismicNextLink>
          ) : (
            <TextLink field={module.link} label={module.link.text} color="moss-grey-550" />
          ))}
      </div>

      {/* Image - pushed to bottom */}
      {isFilled.image(module.image) && (
        <div className="rounded-[4px] border border-moss-white/[10%] bg-grey-100">
          <PrismicNextImage
            field={module.image}
            className={cn('size-full md:max-lg:h-auto md:max-lg:min-w-[180px] md:max-lg:max-w-[180px]', {
              'rounded-[4px]': module.round_image_corners,
            })}
            width={672}
            height={538}
          />
        </div>
      )}
    </div>
  )
}
